import React from 'react'

// Simplified brand glyph paths (24x24 viewBox), used both for the live canvas
// (as inline React SVGs) and for the exported email HTML (as data-URI <img> icons,
// since email clients cannot reliably render inline <svg>).
export const SOCIAL_ICON_PATHS = {
  facebook: 'M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z',
  twitter: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  instagram: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  linkedin: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  youtube: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
}

// Authentic brand colors — used whenever the user hasn't picked a custom Icon Color override.
export const SOCIAL_BRAND_COLORS = {
  facebook: '#1877F2',
  twitter: '#000000', // X (formerly Twitter)
  instagram: '#E4405F', // solid fallback; real gradient applied in render below
  linkedin: '#0A66C2',
  youtube: '#FF0000',
}

const INSTAGRAM_GRADIENT_STOPS = [
  { offset: '0%', color: '#FEDA75' },
  { offset: '30%', color: '#FA7E1E' },
  { offset: '55%', color: '#D62976' },
  { offset: '80%', color: '#962FBF' },
  { offset: '100%', color: '#4F5BD5' },
]

let gradIdCounter = 0

// React icon for the live canvas — crisp inline SVG.
export function SocialIconGlyph({ platform, size = 36, bgColor, iconColor = '#ffffff', isCustomColor = false }) {
  const gradId = React.useMemo(() => `ig-grad-${++gradIdCounter}`, [])
  const path = SOCIAL_ICON_PATHS[platform]
  if (!path) return null
  const iconSize = size * 0.55
  const offset = (size - iconSize) / 2
  const useGradient = platform === 'instagram' && !isCustomColor

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {useGradient && (
        <defs>
          <radialGradient id={gradId} cx="30%" cy="107%" r="150%">
            {INSTAGRAM_GRADIENT_STOPS.map(s => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </radialGradient>
        </defs>
      )}
      <circle cx={size / 2} cy={size / 2} r={size / 2} fill={useGradient ? `url(#${gradId})` : bgColor} />
      <g transform={`translate(${offset}, ${offset}) scale(${iconSize / 24})`} fill={iconColor}>
        <path d={path} />
      </g>
    </svg>
  )
}

// Base64 data-URI <img> source for the exported/raw email HTML.
export function socialIconDataUri(platform, { size = 36, bgColor, iconColor = '#ffffff', isCustomColor = false } = {}) {
  const path = SOCIAL_ICON_PATHS[platform]
  if (!path) return ''
  const iconSize = size * 0.55
  const offset = (size - iconSize) / 2
  const useGradient = platform === 'instagram' && !isCustomColor
  const gradId = 'ig-grad'

  const defs = useGradient
    ? `<defs><radialGradient id="${gradId}" cx="30%" cy="107%" r="150%">`
      + INSTAGRAM_GRADIENT_STOPS.map(s => `<stop offset="${s.offset}" stop-color="${s.color}"/>`).join('')
      + `</radialGradient></defs>`
    : ''
  const fill = useGradient ? `url(#${gradId})` : bgColor

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">`
    + defs
    + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}" fill="${fill}"/>`
    + `<g transform="translate(${offset}, ${offset}) scale(${iconSize / 24})" fill="${iconColor}"><path d="${path}"/></g>`
    + `</svg>`
  const base64 = typeof window !== 'undefined' ? window.btoa(svg) : ''
  return `data:image/svg+xml;base64,${base64}`
}
